/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import {
  ConfigSensorWizardState,
  defaultDefinePropertiesStepData,
  defaultSelectSensorStepData,
  selectAdasProgramsArchive,
  selectSensorBomData,
  selectSensorBomState,
  setConfigSensorWizardState,
  setHasSavedSensorBomData,
  setSensorBomData,
} from "../../../../../redux/ducks/AdasPrograms/slice";
import {MRT_Cell, MRT_ColumnDef, MRT_GlobalFilterTextField, MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleGlobalFilterButton} from "material-react-table";
import {EAdasProgramSensorBomCheckboxTypes, MapSensorTypeToLabel} from "../types";
import {getSensorIcon} from "../adasProgramsHelperFunctions";
import {usePostRequest} from "../../../../../api/helper";
import toast from "react-hot-toast";
import {EAdasProgramsEditSensorDialogType} from "./types";
import {Box, Checkbox, FormControlLabel, IconButton, Tooltip} from "@mui/material";
import ArgosButton from "../../../../components/Buttons/ArgosButton";
import {EUserPermissionAccess} from "../../../../../redux/ducks/AccessControl/types";
import ArgosDialog from "../../../../components/Dialogs/ArgosDialog";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import ArgosMenuItem from "../../../../components/Form/Select/ArgosMenuItem";
import ArgosSelect from "../../../../components/Form/Select/ArgosSelect";
import {Delete as DeleteIcon, Edit as EditIcon} from "@mui/icons-material";
import {tableButtonGray} from "../../../../../constants/colors";
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import cloneDeep from "lodash/cloneDeep";
import ADAS_PROGRAMS_CONSTANTS from "../../constants";
import useGetUrlParams from "../../../../../hooks/useGetUrlParams";
import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";
import { PROPERTY_FIELDS, PROPERTY_KEY } from "./ConfigSensorWizard/DefinePropertiesStep/constants";


interface IProps {
  sensorBomListType: EAdasProgramSensorBomCheckboxTypes
}

const DEFAULT_PROCESSED_RESOLUTION = 0
const DEFAULT_EXPECTED_SYSTEM_LATENCY = 0
/**
 * Process the exclude columns to set default visibility for each column
 * @param inputData - Record<string, boolean> to set initial visibility of columns is optional
 * @returns Record<string, boolean>
 */
export const processExcludeColumns = (inputData: Record<string, boolean>={}) => {
  return Object.entries(PROPERTY_KEY).reduce((acc: Record<string, boolean>, [key, value]) => {
    acc[value] = false;
    return acc;
  }, inputData)
}
export const SensorBillOfMaterialsList = React.memo((props: IProps) => {
  const dispatch = useAppDispatch()
  const hasWriteAccess = useHasWriteAccess() // TODO: careful with dialog vs new tab (although right now same permissions)
  const sensorBomData = useAppSelector(selectSensorBomData)
  const adasProgramId = sensorBomData?.adasProgramId
  let sensorsData = useMemo(() => {
    let _sensorsData: any = {}
    const _sourceSensorsData = sensorBomData?.data?.sensors
    /**
     * filter the data to only show the sensors of the type specified in the props
     */
    if (_sourceSensorsData) {
      Object.keys(_sourceSensorsData).forEach((sensorId: string) => {
        const sensor = _sourceSensorsData[sensorId]
        if (sensor.sensorType === props.sensorBomListType) {
          // _sensorsData = _sensorsData ?? {}
          _sensorsData[sensorId] = sensor
        }
      })
    }
    return _sensorsData
  }, [props.sensorBomListType, sensorBomData])

  const [uniqueSensorLocations, setUniqueSensorLocations] = useState<string[]>([])

  const [tableData, setTableData] = useState<any>([])
  useEffect(() => { // TODO: If you want this to run in one render after edit cell, put in one useeffect or use usememo.
    let _data: any = []
    let _setUniqueSensorLocations = new Set<string>()
    const _uniqueSensorLocations: string[] = []
    let manyLocations: boolean = false
    // map the sensors dictionary to an array of sensors

    if (sensorsData) {
      const sensors = Object.keys(sensorsData).map((sensorId) => {
        const sensor = sensorsData[sensorId]
        const _sensorLocations = sensor.sensorLocations

        if (_sensorLocations.length > 1)
          manyLocations = true
        // loop through sensor locations and add to set
        if (_sensorLocations) {
          _sensorLocations.forEach((sensorLocation: any) => {

              if (!_setUniqueSensorLocations.has(sensorLocation)) {

                // uniqueSensorLocations.current.push(sensorLocation)
                _uniqueSensorLocations.push(sensorLocation.locationKey)
                _setUniqueSensorLocations.add(sensorLocation.locationKey)
              }
            }
          )
        }

        return {
          sensorId,
          sensorType: sensor.sensorType,
          sensorName: sensor.sensorName,
          version: sensor.version,
          sensorLocations: _sensorLocations.length > 1 ? _sensorLocations : [_sensorLocations?.[0]?.['locationKey']],
          notes: _sensorLocations[0]?.['notes'],
          processedResolution: _sensorLocations[0]?.processedResolution, //?? DEFAULT_PROCESSED_RESOLUTION
          notSourced: sensor.notSourced,
          ..._sensorLocations[0]?.properties,
        }
      })
      setUniqueSensorLocations(_uniqueSensorLocations)
      if (manyLocations) {

        const sensorsWithLoc = sensors.filter(sensor => sensor.sensorLocations.length == 1)
        const sensorsWithLocs = sensors.filter(sensor => sensor.sensorLocations.length > 1)
        const transformed = sensorsWithLocs.map(sensor => {
          return sensor.sensorLocations.map((loc: any) => {
            return {...sensor, "sensorLocations": [loc.locationKey], "notes": loc.notes, "processedResolution": loc.processedResolution,
              ...loc.properties};
            //return {...sensor,sensorId:sensor.sensorId+"|"+loc,"sensorLocations":[loc]};
          });
        }).flat();

        _data = [...sensorsWithLoc, ...transformed]
      }
      else {
        _data = sensors
      }
    } else {
      _data = []
    }
    setTableData(_data)
    refTableData.current = _data
  }, [sensorsData])

  const refTableData = useRef<any>([])

  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const reduxIsSensorBomReadOnly = (useAppSelector(state => selectSensorBomState(state).isReadOnly?.[adasProgramId]) ?? false) || reduxIsProgramArchive
  const tableCols = useMemo(() => {
    let _cols: any[] = [
      {header: 'Sensor Type', id: 'sensorType', size: 330,},
      {header: 'Sensor Name', id: 'sensorName', size: 330,},
      {header: 'Sensor Version', id: 'version', size: 180,},
      {header: 'Sensor Locations', id: 'sensorLocations', customFilter: true, size: 300,},
      {header: 'Notes', id: 'notes', enableColumnFilter: false, size: 330,},
      {
        header: 'Processed Resolution (MP)',
        id: 'processedResolution',
        accessorFn: (row: any) => row.processedResolution?.mp ?? DEFAULT_PROCESSED_RESOLUTION,
        enableColumnFilter: false,
        showForSensorTypes: new Set([EAdasProgramSensorBomCheckboxTypes.CAMERAS]),
        size: 200,

        /*muiTableBodyCellEditTextFieldProps: ({cell}: any) => ({
          ...getCommonEditTextFieldProps(cell),
        }),*/
      },
      {
        header: 'Sensor not yet sourced',
        id: 'notSourced',
        enableColumnFilter: false,
      },
      ...PROPERTY_FIELDS.map((field) => ({
        header: field.label,
        id:PROPERTY_KEY[field.id],
        enableColumnFilter: false,
        size: 200,
      })),

    ]
    _cols = _cols.filter((_column) => {
      if (_column.showForSensorTypes) {
        return _column.showForSensorTypes.has(props.sensorBomListType)
      }
      return true
    })
    return _cols
  }, [props.sensorBomListType, sensorBomData, hasWriteAccess, reduxIsSensorBomReadOnly])

  const columns: MRT_ColumnDef<Record<string, any>>[] = useMemo(() => tableCols.map((column: any) => {
    let col: MRT_ColumnDef<Record<string, any>> = {
      // Default values that can be overridden
      enableEditing: false,
      ...column,
      // Default values that won't be overridden
      accessorKey: column.accessorFn ? undefined : `${column.id}`,
      accessorFn: column.accessorFn ? column.accessorFn : undefined,
      Cell: column.accessorFn ? undefined : (cellProps) => {
        if (column.id === 'sensorType') {
          const cellValue: EAdasProgramSensorBomCheckboxTypes = cellProps.row.original[column.id]
          return (
            <div style={{display: 'flex', alignItems: 'center', gap: 3}}>
              {getSensorIcon(cellProps.row.original[column.id])}
              <span>{MapSensorTypeToLabel[cellValue].sensorBomList}</span>
            </div>
          )
        } else if (column.id === 'sensorLocations') {
          return (
            <span>{cellProps.row.original[column.id]}</span>
          )
        }
        else if (column.id === 'notSourced') {
          return (
          <FormControlLabel
            control={<Checkbox
              style={{ justifyContent: "center" }}
              checked={cellProps.row.original[column.id]}
              disabled={true}
            />} label=""
            style={{ justifyContent: "center" }}></FormControlLabel>
          );
        }
        else {
          //if (column.id === 'processedResolution') {
            // console.log(cellProps.renderedCellValue, )
            //return refTableData.current[cellProps.row.index][cellProps.column.id]
          //}
          return cellProps.renderedCellValue
        }
      },
      filterVariant: 'multi-select',
      enableColumnFilter: column.enableColumnFilter ?? true,
      filterSelectOptions: column.customFilter ? uniqueSensorLocations : undefined,
    }
    return col
  }), [uniqueSensorLocations, tableCols])

  const deleteSensorHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=deleteSensor')
  const deleteSensor = (sensorIds: any): Promise<boolean> => {
    if (!adasProgramId || !(sensorIds.length > 0)) return Promise.resolve(false)
    const res = deleteSensorHandle({adasProgramId, sensorIds}).then((response: any) => {
      dispatch(setSensorBomData(response))
      return true
    }).catch((error: any) => {
      console.log(error)
      toast.error('Error deleting sensor(s)')
      return false
    })
    return res

  }

  const removeSensorIdFromLocation = (location: string, sensorId: string, sensorBomDataCopy: any) => {
    const locationKey = location
    const locationData = sensorBomDataCopy.sensorLocations[locationKey].sensors
    const sensorIndex = locationData.indexOf(sensorId)
    if (sensorIndex !== -1) {
      locationData.splice(sensorIndex, 1)
    }
  }
  const [rowSelection, setRowSelection] = useState({});

  const onDeleteRows = () => {
    // map row selection to sensorIds
    const sensorIds = Object.keys(rowSelection).map((rowId, index: number) => {
      const _numRowId: any = Number(rowId)
      return {"sensorId": tableData[_numRowId].sensorId, "locationKey": tableData[_numRowId].sensorLocations.toString()}
    })

    deleteSensor(sensorIds).then((res: boolean) => {
      if (res) {
        setRowSelection({})
      }
    }).catch((error: any) => {
      console.log(error)
    })
  }

  const editSensor = (id: string, modality: string, locationKey: string[], name: string, version: string, notSourced: boolean) => {
    const wizardState: ConfigSensorWizardState = {
      action: EAdasProgramsEditSensorDialogType.EDIT_FROM_TABLE,
      selectSensorStep: {
        ...defaultSelectSensorStepData,
        locationKey, oldLocationKey: locationKey, oldId: id,
        modality, name, version, id, notSourced,
      },
      definePropertiesStep: defaultDefinePropertiesStepData,
    }
    dispatch(setConfigSensorWizardState(wizardState))
  }

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [dialogState, setDialogState] = useState<any>(null)

  const removeSensorFromLocationHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=removeSensorFromLocation')
  const removeSensorFromLocation = useCallback((sensorId: string, locationKey: string[], adasProgramId: string) => {
    // TODO: there is a duplicate for this code in another file
    removeSensorFromLocationHandle({adasProgramId, locationKey, sensorId})
      .then((res: any) => {
        dispatch(setSensorBomData(res))
        setIsDeleteDialogOpen(false)
      }).catch((err: any) => {
      console.log(err)
      toast.error('Error removing sensor from location')
    })
  }, [])

  const callbackDeleteDialogOpen = (sensorId: string, originalSensorLocs: any) => {
    const locations = sensorsData[sensorId].sensorLocations
    setIsDeleteDialogOpen(true)
    setDialogState({locations, sensorId, originalSensorLocs})
  }

  const hasSavedSensorBomData = useAppSelector((state) => state.adasPrograms.sensorBom.hasSavedSensorBomData)
  const saveSensorBomDataHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=setSensorBom')
  const [isSavingSensorBomData, setIsSavingSensorBomData] = useState(false)
  const onSaveChanges = () => {
    setIsSavingSensorBomData(true)
    saveSensorBomDataHandle({sensorBomData}).then((res: any) => {
      dispatch(setHasSavedSensorBomData(true))
    }).catch((err: any) => {
      console.log(err)
      toast.error('Error saving sensor bom data')
    }).finally(() => {
      setIsSavingSensorBomData(false)
    })
  }

  // Default on page load false unselected column keys
  const [columnVisibility, setColumnVisibility] = useState(processExcludeColumns({"notSourced":false}));

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%', overflow: 'auto', zIndex: 2000}}>
      <ArgosTableSimple
        key={'sensorBomTable' + reduxIsSensorBomReadOnly ? 'disabled-select' : 'enabled-select'}
        data={tableData}
        columns={columns}

        initialState={{
          columnVisibility:columnVisibility,
        }}
        enablePagination={true}
        editDisplayMode="cell"
        enableEditing
        renderRowActions={({row}: any) => tableRowActions(row, hasWriteAccess, callbackDeleteDialogOpen, editSensor, reduxIsSensorBomReadOnly)}
        enableRowActions
        positionActionsColumn="last"
        displayColumnDefOptions={{
          'mrt-row-actions': {
            size: 200,
          },
        }}
        renderTopToolbar={({table}) => (
          <Box
            sx={{
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center', backgroundColor: '#F5F6F7',
              width: '100%', paddingLeft: '5px'
            }}
          >
            <div
              style={{justifySelf: 'flex-start', marginRight: 'auto'}}
            >
              <ArgosButton
                hidden={!hasWriteAccess}
                disabled={!Object.keys(rowSelection).length || reduxIsSensorBomReadOnly}
                onClick={onDeleteRows}
              >
                Delete Rows
                ({Object.keys(rowSelection).length})
              </ArgosButton>
            </div>
            {
              /* props.sensorBomListType === EAdasProgramSensorBomCheckboxTypes.CAMERAS &&
               <div style={{justifySelf: 'flex-end'}}>
                 <ArgosButton isLoadingButton={true} isLoading={isSavingSensorBomData}
                              hidden={!hasWriteAccess}
                              disabled={hasSavedSensorBomData || isSavingSensorBomData || reduxIsSensorBomReadOnly}
                              onClick={onSaveChanges}>
                   <span style={{marginRight: isSavingSensorBomData ? 20 : 0}}>
                     Save Changes
                   </span>
                 </ArgosButton>
               </div>*/
            }
            <Box style={{justifySelf: 'flex-end'}}>
              {tableToolbarActions(table, true, EUserPermissionAccess.READ_WRITE, dispatch)}
            </Box>
          </Box>
        )}

        enableFacetedValues
        enableMultiRowSelection={true}
        enableRowSelection={!reduxIsSensorBomReadOnly && hasWriteAccess}
        onRowSelectionChange={setRowSelection}
        state={{rowSelection, isLoading: isSavingSensorBomData}}
      />
      <ArgosDialog
        maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
        }}
        headerTitle={'Delete sensor from location'}
        headerTitleAlign={'left'}
      >
        <DeleteDialog
          dialogState={dialogState}
          removeSensorFromLocation={(_locationsToDelete: string[], sensorId: string) => removeSensorFromLocation(sensorId, _locationsToDelete, adasProgramId)}
          setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        />
      </ArgosDialog>
    </ArgosFlexbox>
  )
})

const DeleteDialog = (props: any) => {
  const {dialogState, removeSensorFromLocation, setIsDeleteDialogOpen} = props
  const {sensorId, locations, originalSensorLocs} = dialogState
  const [locationsToDelete, setLocationsToDelete] = useState<string[]>(locations ?? [])
  useEffect(() => {
    setLocationsToDelete(originalSensorLocs)
    //setLocationsToDelete(originalSensorLocs.map((location: any) => {return location.locationKey}))
  }, [locations])

  const {adasProgramId} = useGetUrlParams()
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const reduxIsSensorBomReadOnly = (useAppSelector(state => selectSensorBomState(state).isReadOnly?.[adasProgramId]) ?? false) || reduxIsProgramArchive
  const isDeleteDisabled = reduxIsSensorBomReadOnly || reduxIsProgramArchive
  return (
    <ArgosFlexbox vertical>
      <ArgosSelect
        value={locationsToDelete}
        multiple={true}
        label="Sensor Location"
        onChange={(e: any) => {
          const {value} = e.target;
          const val = typeof value === 'string' ? value.split(',') : value
          setLocationsToDelete((prevState: any) => (val))
        }}
      >
        {locations.map((location: any) => (
            <ArgosMenuItem value={location.locationKey}>{location.locationKey}</ArgosMenuItem>
          )
        )}
      </ArgosSelect>
      <ArgosFlexbox justifyContent={'center'}>
        <ArgosButton
          onClick={() => {
            setIsDeleteDialogOpen(false)
          }}
        >
          Cancel
        </ArgosButton>
        {reduxIsSensorBomReadOnly ? (
          <Tooltip
            title={ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.sensorBomReadOnlyDevStagesMessage}
            placement={'bottom'}
          >
            <div>
              <ArgosButton
                disabled={true}
                onClick={() => {
                  removeSensorFromLocation(locationsToDelete, sensorId)
                }}
              >
                Delete
              </ArgosButton>
            </div>
          </Tooltip>
        ) : (
          <div>
            <ArgosButton
              onClick={() => {
                removeSensorFromLocation(locationsToDelete, sensorId)
              }}
              disabled={isDeleteDisabled}
            >
              Delete
            </ArgosButton>
          </div>
        )}
      </ArgosFlexbox>
    </ArgosFlexbox>
  )
}

const tableRowActions = (row: any, hasWriteAccess: boolean, setIsDeleteDialogOpen: any, editSensor: any, readOnly: boolean) => {
  const id: string = row.original.sensorId
  return (
    <Box
      sx={{
        display: row.subRows?.length ? 'none' : 'flex',
        flexWrap: 'nowrap'
      }}
    >
      {hasWriteAccess && <Tooltip title={"Delete"} arrow>
        <IconButton
          size="large"
          disabled={readOnly}
          onClick={(e) => {
            setIsDeleteDialogOpen(id, row.original.sensorLocations)
          }}
        >
          <DeleteIcon fontSize="medium" style={{color: !readOnly ? tableButtonGray : undefined}}
                      color={readOnly ? 'disabled' : undefined}/>
        </IconButton>
      </Tooltip>}
      {hasWriteAccess && <Tooltip title={"Edit"} arrow>
        <IconButton
          size="large"
          disabled={readOnly}
          onClick={(e) => {
            const locationKey = row.original.sensorLocations
            const modality = row.original.sensorType
            const name = row.original.sensorName
            const version = row.original.version
            const notSourced = id === "" || id.endsWith("-not-sourced")
            editSensor(id, modality, locationKey, name, version, notSourced)
          }}
        >
          <EditIcon fontSize="medium" style={{color: !readOnly ? tableButtonGray : undefined}}
                    color={readOnly ? 'disabled' : undefined}/>
        </IconButton>
      </Tooltip>}
    </Box>
  )
}

const tableToolbarActions = (table: any, isAdmin: boolean, userAccessLevel: string, dispatch: any) => {
  const hasWriteAccess: boolean = userAccessLevel === EUserPermissionAccess.READ_WRITE || isAdmin

  return (
    <ArgosFlexbox>
      <MRT_GlobalFilterTextField table={table}/>
      <MRT_ToggleGlobalFilterButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ToggleFiltersButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ShowHideColumnsButton table={table} style={{color: tableButtonGray}}/>
    </ArgosFlexbox>
  )
}